"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { motion } from "motion/react";
import {
  Calendar,
  Clock,
  Users,
  Star,
  CheckCircle2,
  ArrowRight,
  Package as PackageIcon,
  MapPin,
  Utensils,
  Hotel,
  Car,
  ChevronDown,
  Flame,
} from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import {
  createLookupObject,
  isValidImgSrc,
  formatCurrency,
  cleanHeaderTags,
} from "@/_helper/helper";
import { useAppSelector } from "@/_hooks/hooks";
import { ICMS, ISettings } from "@/store/homeSlice";

function PackagesContent({
  PackageList,
  Path,
}: {
  PackageList: any[];
  Path: string;
}) {
  const router = useRouter();
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const SettingList = useAppSelector((state) => state.home.SettingList);

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;

  const banner: any =
    BannerList?.find((banner: any) => banner?.BannerType === Path) || {};

  const [selectedNightFilter, setSelectedNightFilter] = useState("All");

  const filteredPackages = PackageList?.filter((pkg) => {
    if (selectedNightFilter === "All") return true;
    const nights = Number(pkg?.NoOfNights) || 0;
    if (selectedNightFilter === "1") return nights === 1;
    if (selectedNightFilter === "2") return nights === 2;
    if (selectedNightFilter === "3") return nights === 3;
    if (selectedNightFilter === "1 to 3") return nights >= 1 && nights <= 3;
    if (selectedNightFilter === "4 to 7") return nights >= 4 && nights <= 7;
    if (selectedNightFilter === "8 to 12") return nights >= 8 && nights <= 12;
    if (selectedNightFilter === "more than 12") return nights > 12;
    return true;
  });

  return (
    <>
      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden shrink-0">
        {isValidImgSrc(banner?.BannerImage) && (
          <Image
            src={banner?.BannerImage || ""}
            alt={banner?.BannerTitle || "Banner Image"}
            fill
            className="object-cover"
            priority
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

        <div className="absolute inset-0 flex items-end pointer-events-none">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {banner?.SubTitle && (
                <div className="inline-flex text-white items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                  <PackageIcon className="w-5 h-5" />
                  <span className="font-semibold">{banner?.SubTitle}</span>
                </div>
              )}
              <h1 className="text-5xl md:text-7xl font-bold text-amber-300 mb-6 drop-shadow-2xl">
                {banner?.BannerTitle || "Tour Packages"}
              </h1>
              {banner?.Description && (
                <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto drop-shadow-lg mb-6">
                  {banner?.Description}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="bg-white border-b sticky top-[80px] z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="font-semibold text-lg">Filter by Nights</span>
            </div>
            <div className="relative w-full md:w-72">
              <select
                value={selectedNightFilter}
                onChange={(e) => setSelectedNightFilter(e.target.value)}
                className="w-full appearance-none bg-orange-50/50 border-2 border-orange-100 text-gray-900 text-sm rounded-xl focus:ring-orange-500 focus:border-orange-500 block p-3 pr-10 outline-none transition-all cursor-pointer hover:bg-orange-100/50 font-medium"
              >
                <option value="All">All Packages</option>
                {Path === "sou-packages" ? (
                  <>
                    <option value="1">1 Night</option>
                    <option value="2">2 Nights</option>
                    <option value="3">3 Nights</option>
                  </>
                ) : (
                  <>
                    <option value="1 to 3">1 to 3 Nights</option>
                    <option value="4 to 7">4 to 7 Nights</option>
                    <option value="8 to 12">8 to 12 Nights</option>
                    <option value="more than 12">More than 12 Nights</option>
                  </>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-orange-600">
                <ChevronDown className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 min-h-[40vh]">
        {filteredPackages?.length > 0 ? (
          <div className="grid lg:grid-cols-2 gap-8">
            {filteredPackages?.map((pkg, index) => (
              <motion.div
                key={pkg.ID}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full relative"
              >
                {pkg?.IsPopular && (
                  <div className="ribbon flex items-center gap-1 py-1 uppercase">
                    <Flame className="w-3.5 h-3.5 fill-white animate-pulse" />
                    <span className="tracking-wider text-sm md:text-md">
                      Highest Selling
                    </span>
                  </div>
                )}
                <Link
                  href={`/${Path}/${pkg?.TourURL}`}
                  className={`group block bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500  border overflow-hidden ${
                    pkg?.IsPopular
                      ? "border-orange-400 ring-1 ring-amber-500/20 shadow-[0_10px_30px_rgba(251,191,36,0.15)] scale-[1.01]"
                      : "border-gray-100 hover:border-orange-200"
                  } h-full flex flex-col`}
                >
                  {/* Image Section */}
                  <div className="relative aspect-[16/9] overflow-hidden shrink-0">
                    <Image
                      src={
                        isValidImgSrc(pkg?.MainImage)
                          ? pkg.MainImage
                          : "/placeholder.jpg"
                      }
                      alt={pkg?.TourName}
                      fill
                      className="object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none"></div> */}

                    {/* Badge */}
                    {/* <div
                    className={`absolute top-4 left-4 bg-gradient-to-r ${pkg?.badgeColor} text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg`}
                  >
                    {pkg?.badge}
                  </div> */}

                    {/* Rating */}
                    {/* <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-bold text-gray-900">
                      {pkg?.rating}
                    </span>
                    <span className="text-gray-600 text-sm">
                      ({pkg?.reviews})
                    </span>
                  </div> */}

                    {/* Duration Badge */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg">
                      <Calendar className="w-5 h-5 text-orange-600" />
                      <span className="font-bold text-gray-900">
                        {`${pkg?.NoOfNights || 0} Nights / ${pkg?.NoOfDays || 0} Days`}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {pkg?.TourName}
                    </h3>
                    {/* <p className="text-orange-600 font-semibold mb-3">
                    {pkg?.subtitle}
                  </p> */}
                    <p
                      className="text-gray-600 mb-4 line-clamp-3 min-h-[3rem] blog-content"
                      dangerouslySetInnerHTML={{
                        __html: cleanHeaderTags(pkg?.ShortDescription),
                      }}
                    />

                    {/* Inclusions */}
                    {/* {pkg?.KeyPoints && (
                    <div className="mb-4">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">
                        Package Includes:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {pkg?.KeyPoints?.split(",")?.map(
                          (highlight: any, i: number) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium"
                            >
                              <CheckCircle2 className="w-3 h-3" />
                              {highlight}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  )} */}

                    {/* Meta Info */}
                    {/* <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 mt-auto">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-orange-600" />
                      <span>{pkg?.groupSize}</span>
                    </div>
                  </div> */}

                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 flex-wrap mt-auto">
                      <div>
                        {pkg?.PackagePrices?.[0]?.CoupleCost &&
                        +pkg?.PackagePrices?.[0]?.CoupleCost > 0 ? (
                          <div className="flex items-center gap-2">
                            <span className="text-3xl font-bold text-orange-600">
                              {formatCurrency(
                                pkg?.PackagePrices?.[0]?.CoupleCost || 0,
                              )}
                            </span>
                            <span className="text-gray-600">/ per couple</span>
                          </div>
                        ) : (
                          <div className="text-sm ">
                            For Rate Call us on :
                            <span className="block text-lg font-bold text-orange-600">
                              <Link
                                href={`tel:${PhoneNo}`}
                                className="hover:underline"
                              >
                                {PhoneNo}
                              </Link>
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-orange-600 font-semibold">
                        <span>View Details</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="bg-orange-50 p-6 rounded-full mb-4">
              <PackageIcon className="w-12 h-12 text-orange-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No Packages Found
            </h3>
            <p className="text-gray-600 max-w-md">
              We couldn't find any packages for the selected duration. Try
              selecting a different range or view all packages.
            </p>
            {PackageList && PackageList?.length > 0 ? (
              <button
                onClick={() => setSelectedNightFilter("All")}
                className="mt-6 px-6 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
              >
                Show All Packages
              </button>
            ) : (
              <button
                onClick={() => router.push("/")}
                className="mt-6 px-6 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
              >
                Go to Home
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
}

export default function PackagesClient({
  PackageList,
  Path,
  cmsData,
}: {
  PackageList: any[];
  Path: string;
  cmsData: ICMS[];
}) {
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-orange-50/30 to-white">
      <Suspense
        fallback={
          <div className="h-[50vh] flex items-center justify-center">
            Loading packages...
          </div>
        }
      >
        <PackagesContent PackageList={PackageList} Path={Path} />
      </Suspense>

      {/* Features Section */}
      {lookUpCMSData?.["Packages - Section 1: What's Included"] && (
        <section className="bg-gradient-to-br from-orange-50 to-amber-50 py-16">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 blog-content"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData?.["Packages - Section 1: What's Included"]
                  ?.Template,
              ),
            }}
          ></div>
        </section>
      )}
      {/* <section className="bg-gradient-to-br from-orange-50 to-amber-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            What's Included in Our Packages
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: Hotel,
                title: "Accommodation",
                desc: "Comfortable stays at selected hotels",
              },
              {
                icon: Utensils,
                title: "Meals",
                desc: "Delicious breakfast, lunch & dinner",
              },
              {
                icon: Car,
                title: "Transportation",
                desc: "AC vehicle for all transfers",
              },
              {
                icon: MapPin,
                title: "Attractions",
                desc: "Entry to all major sightseeing spots",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center shadow-lg"
              >
                <feature.icon className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {lookUpCMSData?.["Packages - Section 2: Contact Us"] && (
        <section className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 text-white py-16">
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center blog-content"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(
                lookUpCMSData?.["Packages - Section 2: Contact Us"]?.Template,
              ),
            }}
          ></div>
        </section>
      )}
      {/* <section className="bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <PackageIcon className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need a Custom Package?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We can create a personalized itinerary based on your preferences and
            budget
          </p>
          <Link
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-orange-600 font-bold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
          >
            Contact Us for Custom Package
          </Link>
        </div>
      </section> */}
    </div>
  );
}
